import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useBoolean } from 'react-use'
import clsx from 'clsx'

import { TypographyTextTransform } from '@makyoapp/makyo-common'
import { Reset24 } from '@makyoapp/makyo-icon/icon'
import { DEFAULT_MATERIAL_TYPOGRAPHY } from '@makyoapp/makyo-tw-ui'
import { DeleteModal, Field, JustifyVariant, Modal, SelectDropdown, Slider } from '@makyoapp/makyo-ui'

import { useAppBrandKitContext } from '@/context/AppBrandKitContext'
import { useCacheContext } from '@/context/CacheContext'
import { useBrandkit } from '@/hooks/useBrandkit'
import { useReqError } from '@/hooks/useReqError'
import {
  BrandKit,
  convertToRem,
  MT_BREAKPOINTS,
  MT_HTML_TAGS,
  MT_SIZE_OPTIONS,
  MT_TYPE_TYPE,
  MtTypographyKey,
} from '@/shared/brand-kit'
import { familyStyleToText } from '@/shared/gapi'
import { toPascalCase } from '@/shared/utils'

import { FontSelectOptimized } from '../FontSelect/FontSelectOptimized'
import { Info } from '../Info'
import { SaveButton } from '../SaveButton'

interface FieldProps {
  label: string
  children: React.ReactNode
}

export function BrandKitFontModalV2({ show, setShow }: { show: string; setShow: (next: string) => void }) {
  const { t } = useTranslation()
  const {
    isNavigationBar,
    isHTML,
    draftBrandKit,
    setDraftBrandKit,
    activeSize,
    setActiveSize,
    activeTypography,
    setActiveTypography,
    variants,
    getTypographyValueSafe,
  } = useBrandkit()

  const { handleUpdateBrandKit } = useAppBrandKitContext()

  const { isFontLoading } = useCacheContext()
  const printAPIError = useReqError()

  const [step, setStep] = useState(1)
  const [isLoading, setIsLoading] = useBoolean(false)
  const [isResetModalOpen, setIsResetModalOpen] = useBoolean(false)
  const [isResetChoiceModalOpen, setIsResetChoiceModalOpen] = useBoolean(false)
  const [resetMode, setResetMode] = useState<'all' | 'group' | null>(null)

  const removeSpaces = (str: string) => str.replace(/\s+/g, '')

  const normalizeTypographyForKey = (ty: string | undefined) => {
    if (!ty) return ty
    const normalized = removeSpaces(ty).toLowerCase()
    return normalized === 'richtext' ? 'html' : normalized
  }

  const selectedSize = isNavigationBar ? toPascalCase(activeSize || '') : activeSize

  const tyKey =
    `${normalizeTypographyForKey(activeTypography)}${removeSpaces(selectedSize as string)}` as MtTypographyKey

  const valueSafe = getTypographyValueSafe(tyKey as MtTypographyKey, draftBrandKit)

  const [newFFName, setNewFFName] = useState('')

  const handleFontChange = (familyName: string) => {
    if (draftBrandKit) {
      const updatedTypography = {
        [tyKey]: {
          ...draftBrandKit.typography[tyKey],
          familyName,
          familyStyle: 'regular',
          weight: 400,
        },
      }

      setNewFFName(familyName)

      setDraftBrandKit({
        ...draftBrandKit,
        typography: {
          ...draftBrandKit.typography,
          ...updatedTypography,
        },
      })
    }
  }

  const handleTypographyChange = (
    property: 'lineHeight' | 'letterSpacing' | 'familyStyle' | 'textTransform' | 'size',
    value: string | number
  ) => {
    setDraftBrandKit((prevCopy) => {
      const payload: BrandKit | undefined = prevCopy && {
        ...prevCopy,
        typography: {
          ...prevCopy.typography,
          [tyKey as string]: {
            ...prevCopy.typography[tyKey as MtTypographyKey],
            [property]: value,
            ...(property === 'familyStyle' && {
              weight:
                value.toString().toLowerCase() === 'regular' ? 400 : Number(value.toString().replace('italic', '')),
            }),
          },
        },
      }
      return payload
    })
  }

  const handleTextTransformChange = (opt: string) => handleTypographyChange('textTransform', opt)
  const handleLineHeightChange = (opt: number) => handleTypographyChange('lineHeight', opt)
  const handleLetterSpacingChange = (opt: number) => handleTypographyChange('letterSpacing', opt)
  const handleStyleChange = (opt: string) => handleTypographyChange('familyStyle', opt)
  const handleSizeChange = (opt: number) => handleTypographyChange('size', opt)

  const getBreakpointLabel = (value: string) =>
    MT_BREAKPOINTS.find((item) => item.value === value)?.label || 'Mobile (Portrait)'

  const getBreakpointValue = (label: string) => MT_BREAKPOINTS.find((item) => item.label === label)?.value || 'phone'

  const getSizeField = () => {
    if (isHTML) {
      return {
        label: 'HTML Tag',
        options: MT_HTML_TAGS.map((item) => item.label),
        value: activeSize,
        renderOption: (x: string) => x,
      }
    }

    return {
      label: 'Size',
      options: MT_SIZE_OPTIONS.map((item) => item.label),
      value: activeSize,
      renderOption: (x: string) => x,
    }
  }

  const FormField: React.FC<FieldProps> = ({ label, children }) => (
    <Field isVertical className='w-full'>
      <label>
        <Trans>{label}</Trans>
      </label>
      {children}
    </Field>
  )

  const handleApplyAllChanges = () => {
    if (!draftBrandKit) return

    const typography = draftBrandKit.typography
    const activeTypoKey = normalizeTypographyForKey(activeTypography)

    const getGroupedTypography = Object.keys(typography).reduce(
      (acc, key) => {
        const prefix = key.match(/^[a-z]+/)?.[0] || ''
        if (!acc[prefix]) acc[prefix] = []
        acc[prefix].push(key)
        return acc
      },
      {} as Record<string, string[]>
    )

    const updatedTypographyKeys = getGroupedTypography[activeTypoKey as string]
    const newTypography = updatedTypographyKeys.reduce(
      (acc, key) => {
        acc[key] = {
          ...(typography as any)[key],
          familyName: newFFName,
        }
        return acc
      },
      {} as Record<string, any>
    )

    const mergeTypography = (target: any, source: any) => {
      Object.keys(target).forEach((key) => {
        if (source[key]) {
          target[key] = {
            ...target[key],
            ...source[key],
          }
        }
      })
      return target
    }
    setNewFFName('')

    const typoPayload = mergeTypography(typography, newTypography)
    setDraftBrandKit({
      ...draftBrandKit,
      typography: typoPayload,
    })
  }

  const handleResetBrandFonts = async () => {
    if (draftBrandKit) {
      const updatedBrandKit = {
        ...draftBrandKit,
        typography: {
          ...draftBrandKit.typography,
          ...DEFAULT_MATERIAL_TYPOGRAPHY,
        },
      }

      setDraftBrandKit(updatedBrandKit)

      handleUpdateBrandKit(updatedBrandKit, {
        description: 'Update brand fonts',
      })
        .catch(printAPIError)
        .finally(() => {
          setIsResetModalOpen(false)
          setIsResetChoiceModalOpen(false)
          setResetMode(null)
        })
    }
  }

  const handleResetBrandFontsByGroup = async () => {
    if (draftBrandKit && activeTypography) {
      const activeTypoKey = normalizeTypographyForKey(activeTypography)

      const groupKeys = Object.keys(DEFAULT_MATERIAL_TYPOGRAPHY).filter((key) => {
        const prefix = key.match(/^[a-z]+/)?.[0] || ''
        return prefix === activeTypoKey
      }) as MtTypographyKey[]

      const groupTypography = groupKeys.reduce(
        (acc, key) => {
          acc[key] = DEFAULT_MATERIAL_TYPOGRAPHY[key]
          return acc
        },
        {} as Partial<typeof DEFAULT_MATERIAL_TYPOGRAPHY>
      )

      const updatedBrandKit = {
        ...draftBrandKit,
        typography: {
          ...draftBrandKit.typography,
          ...groupTypography,
        },
      }

      setDraftBrandKit(updatedBrandKit)

      handleUpdateBrandKit(updatedBrandKit, {
        description: `Update brand fonts for ${activeTypography} group`,
      })
        .catch(printAPIError)
        .finally(() => {
          setIsResetModalOpen(false)
          setIsResetChoiceModalOpen(false)
          setResetMode(null)
        })
    }
  }

  const ResetChoiceDialog = () => (
    <Modal
      show={isResetChoiceModalOpen}
      onHide={() => {
        setIsResetChoiceModalOpen(false)
        setResetMode(null)
      }}
    >
      <Modal.Header
        onCloseClick={() => {
          setIsResetChoiceModalOpen(false)
          setResetMode(null)
        }}
      >
        <Trans>Restore Default Typography</Trans>
      </Modal.Header>
      <Modal.Body>
        <div className='text-sm modal-md'>
          <p className='mb-4 leading-relaxed'>
            <Trans>What would you like to restore?</Trans>
          </p>
          <div className='flex gap-3'>
            <button
              className='bg-teal-600 hover:bg-teal-500 transition-colors duration-150 text-white shadow-md py-3 px-4 rounded-md flex items-center justify-center w-full'
              onClick={() => {
                setResetMode('all')
                setIsResetChoiceModalOpen(false)
                setIsResetModalOpen(true)
              }}
            >
              <Trans>Restore All Styles</Trans>
            </button>
            <button
              className='bg-blue-600 hover:bg-blue-500 transition-colors duration-150 text-white shadow-md py-3 px-4 rounded-md flex items-center justify-center w-full'
              onClick={() => {
                setResetMode('group')
                setIsResetChoiceModalOpen(false)
                setIsResetModalOpen(true)
              }}
            >
              <Trans>Restore {activeTypography} Only</Trans>
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )

  const ConfirmResetDialog = () => (
    <DeleteModal
      withCancel
      continueLabel={t('Restore Defaults')}
      show={isResetModalOpen}
      title={t('Restore Default Settings')}
      onHide={() => {
        setIsResetModalOpen(false)
        setResetMode(null)
      }}
      onSubmit={() => {
        if (resetMode === 'all') {
          handleResetBrandFonts()
        } else if (resetMode === 'group') {
          handleResetBrandFontsByGroup()
        }
      }}
    >
      <p className='leading-relaxed'>
        {resetMode === 'all' ? (
          <>
            Resetting the typography in this brand kit will restore all settings to their original defaults, erasing any
            customizations made so far. This will not affect custom typography inside the app builder. Do you want to
            proceed?
          </>
        ) : (
          <>
            Resetting the {activeTypography} group will restore all typography settings in this group to their original
            defaults, erasing any customizations made to this group. This will not affect other typography groups or
            custom typography inside the app builder. Do you want to proceed?
          </>
        )}
      </p>
    </DeleteModal>
  )

  return step < 1 ? (
    <DeleteModal
      withCancel
      continueLabel={t('Yes, Change')}
      show={true}
      title={t('Change Default Brand Fonts?')}
      onHide={() => setShow('')}
      onSubmit={() => setStep(1)}
    >
      <div className='text-base modal-md'>
        Are you sure you want to make changes to the default brand fonts? Please note that any changes made to these
        fonts will impact multiple components. For a safer approach, consider modifying fonts for individual instances
        within the app builder.
      </div>
    </DeleteModal>
  ) : (
    <>
      <ResetChoiceDialog />
      <ConfirmResetDialog />
      <Modal show={Boolean(show)} onHide={() => setShow('')}>
        <Modal.Header onCloseClick={() => setShow('')}>
          <Trans>Global Font Styles Editor</Trans>
        </Modal.Header>
        <Modal.Body>
          {isFontLoading ? (
            <span className='loading spin' />
          ) : (
            <div className='modal-lg grid grid-cols-1 sm:grid-cols-12 gap-4 sm:gap-6'>
              <div className='sm:col-span-4 flex flex-col py-2 text-white h-full'>
                <div className='sticky top-8'>
                  <button
                    className='bg-teal-600 hover:bg-teal-500 transition-colors duration-150 text-white font-semibold shadow-md py-2 px-4 rounded-md mb-4 flex items-center gap-2'
                    onClick={() => setIsResetChoiceModalOpen(true)}
                  >
                    <Reset24 className='w-4' />
                    <Trans>Restore Default Settings</Trans>
                  </button>
                  <ul>
                    {MT_TYPE_TYPE.map((ty) => {
                      const isActive = activeTypography === removeSpaces(ty)
                      const buttonClasses = clsx(
                        'w-full text-left font-bold px-4 py-2 mb-2 rounded-sm transition-colors duration-200',
                        isActive
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      )
                      return (
                        <li
                          key={ty}
                          role='button'
                          onClick={() => {
                            const newActiveTypography = removeSpaces(ty)
                            if (newActiveTypography === 'NavigationBar') setActiveSize(MT_BREAKPOINTS[0].value)
                            setActiveTypography(newActiveTypography)
                          }}
                          className={buttonClasses}
                        >
                          {ty}
                        </li>
                      )
                    })}
                  </ul>
                  <Info isDanger className='border border-red-600 rounded-md p-4 mt-4'>
                    Please note that any changes made to these fonts will impact multiple components. For a safer
                    approach, consider modifying fonts for individual instances within the app builder.
                  </Info>
                </div>
              </div>
              <div className='sm:col-span-8'>
                <div className='mt-4 border border-slate-200 dark:border-slate-600 px-3 rounded-md overflow-hidden'>
                  <div className='w-full grid grid-cols-2 gap-4 sm:gap-6'>
                    <div>
                      <FormField label='Font'>
                        <FontSelectOptimized
                          id='font-select'
                          value={valueSafe?.familyName}
                          onChange={handleFontChange}
                        />
                      </FormField>
                      {newFFName && (
                        <button
                          className='bg-teal-500 text-white text-xs py-1 w-full rounded-md'
                          onClick={handleApplyAllChanges}
                        >
                          Apply changes to all
                        </button>
                      )}
                    </div>
                    {isNavigationBar && (
                      <FormField label='Device'>
                        <SelectDropdown
                          options={MT_BREAKPOINTS.map((item) => item.label)}
                          onChange={(opt) => setActiveSize(getBreakpointValue(opt))}
                          value={getBreakpointLabel(activeSize || '')}
                        />
                      </FormField>
                    )}
                    {!isNavigationBar && (
                      <FormField label={getSizeField().label}>
                        <SelectDropdown
                          {...getSizeField()}
                          onChange={(opt) => {
                            let next = opt
                            if (isHTML) {
                              next = MT_HTML_TAGS.find((item) => item.label === opt)?.value || 'H1'
                            }
                            setActiveSize(next)
                          }}
                        />
                      </FormField>
                    )}
                  </div>
                  <div className='w-full flex items-center gap-4 sm:gap-6'>
                    <Field isVertical className='w-1/2'>
                      <label>
                        <Trans>Style</Trans>
                      </label>
                      <SelectDropdown
                        onChange={handleStyleChange}
                        value={`${valueSafe?.familyStyle}`}
                        renderOption={familyStyleToText}
                        options={variants}
                      />
                    </Field>
                    <Field isVertical className='w-1/2'>
                      <label>
                        <Trans>Case</Trans>
                      </label>
                      <SelectDropdown
                        onChange={handleTextTransformChange}
                        value={valueSafe?.textTransform as TypographyTextTransform}
                        renderOption={(x) => x}
                        options={Object.values(TypographyTextTransform)}
                      />
                    </Field>
                  </div>
                  <Field isVertical className='gap-0'>
                    <div className='w-full flex justify-between'>
                      <label>
                        <Trans>Size</Trans>
                      </label>
                      <p className='text-xs text-light'>{convertToRem(valueSafe?.size)}rem</p>
                    </div>
                    <div className='w-full'>
                      <Slider
                        values={[convertToRem(valueSafe?.size)]}
                        step={0.05}
                        min={0}
                        max={10}
                        onChange={(opt) => handleSizeChange(opt[0])}
                      />
                    </div>
                  </Field>
                  <Field isVertical className='gap-0'>
                    <div className='w-full flex justify-between'>
                      <label>
                        <Trans>Line Height</Trans>
                      </label>
                      <p className='text-xs text-light'>{convertToRem(valueSafe?.lineHeight)}rem</p>
                    </div>
                    <Slider
                      className='w-full'
                      values={[convertToRem(valueSafe?.lineHeight)]}
                      step={0.05}
                      min={0}
                      max={10}
                      onChange={(values) => handleLineHeightChange(values[0])}
                    />
                  </Field>
                  <Field isVertical className='gap-0'>
                    <div className='w-full flex justify-between'>
                      <label>
                        <Trans>Letter Spacing</Trans>
                      </label>
                      <p className='text-xs text-light'>
                        {convertToRem(valueSafe?.letterSpacing ? +valueSafe.letterSpacing : 0)}rem
                      </p>
                    </div>
                    <div className='w-full'>
                      <Slider
                        values={[convertToRem(valueSafe?.letterSpacing ? +valueSafe.letterSpacing : 0)]}
                        step={0.05}
                        min={0}
                        max={10}
                        onChange={(opt) => handleLetterSpacingChange(opt[0])}
                      />
                    </div>
                  </Field>
                  <div className='-m-3 p-3 pb-6 dark:bg-slate-900 bg-slate-100 mt-4'>
                    <p
                      className='dark:text-white text-gray-800'
                      style={{
                        fontFamily: valueSafe?.familyName || 'inherit',
                        fontWeight: valueSafe?.weight || 'normal',
                        fontStyle: valueSafe?.familyStyle || 'normal',
                        fontSize: `${convertToRem(valueSafe?.size ? +valueSafe?.size : 0)}rem`,
                        lineHeight: `${convertToRem(valueSafe?.lineHeight ? +valueSafe?.lineHeight : 0)}rem`,
                        letterSpacing: `${convertToRem(valueSafe?.letterSpacing ? +valueSafe?.letterSpacing : 0)}rem`,
                        textTransform: (valueSafe?.textTransform as any) || 'none',
                      }}
                    >
                      This is a preview of the font style.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer justify={JustifyVariant.End}>
          <SaveButton
            isLoading={isLoading}
            onClick={() => {
              if (draftBrandKit) {
                setIsLoading(true)
                handleUpdateBrandKit(draftBrandKit, {
                  description: 'Update brand fonts',
                })
                  .then(() => {
                    toast.success('Brand fonts updated')
                  })
                  .catch(printAPIError)
                  .finally(() => {
                    setIsLoading(false)
                    setShow('')
                  })
              }
            }}
          />
        </Modal.Footer>
      </Modal>
    </>
  )
}
